import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMarketing, EmailTemplate } from "../../../context/MarketingContext";
import { formatApiErrors } from "../../../services/marketingTemplatesApi";
import { Input } from "../../../components/ui/input2";
import { Label } from "../../../components/ui/Label";
import { Textarea } from "../../../components/ui/Textarea";
import RichTextEditor from "../../../components/ui/RichTextEditor";
import { Button } from "../../../components/ui/Button";
import { useToast } from "../../../hooks/useToast";

interface EmailTemplateFormProps {
  template?: EmailTemplate | null;
}

export const EmailTemplateForm: React.FC<EmailTemplateFormProps> = ({ template }) => {
  const isEdit = Boolean(template);
  const navigate = useNavigate();
  const toast = useToast();
  const { addEmailTemplate, updateEmailTemplate } = useMarketing();

  const [name, setName] = useState(template?.name ?? "");
  const [description, setDescription] = useState(template?.description ?? "");
  const [html, setHtml] = useState(template?.html ?? "");

  useEffect(() => {
    setName(template?.name ?? "");
    setDescription(template?.description ?? "");
    setHtml(template?.html ?? "");
  }, [template?.id, template?.name, template?.description, template?.html]);

  const [errors, setErrors] = useState<{ name?: string; description?: string; html?: string }>({});
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const nextErrors: typeof errors = {};
    if (!name.trim()) {
      nextErrors.name = "Template name is required.";
    }
    if (!description.trim()) {
      nextErrors.description = "Description is required.";
    }
    if (!html.trim()) {
      nextErrors.html = "Template content is required.";
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    try {
      setSaving(true);
      if (isEdit && template) {
        await updateEmailTemplate(template.id, {
          name: name.trim(),
          description: description.trim(),
          html,
        });
        toast.success("Email template updated.");
      } else {
        await addEmailTemplate({
          name: name.trim(),
          description: description.trim(),
          html,
        });
        toast.success("Email template created.");
      }
      navigate("/marketing/email-templates");
    } catch (err) {
      toast.error(formatApiErrors(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-4xl mx-auto bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6 space-y-5"
    >
      <div className="space-y-1.5">
        <Label htmlFor="name">Template Name</Label>
        <Input
          id="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Welcome Email"
        />
        {errors.name && (
          <p className="text-xs text-red-500 mt-1">{errors.name}</p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Short internal description of this template."
          resize="vertical"
          error={errors.description}
        />
      </div>

      <div className="space-y-2">
        <Label>Template Builder (HTML)</Label>
        <RichTextEditor
          value={html}
          onChange={setHtml}
          placeholder="Compose your email content..."
        />
        {errors.html && (
          <p className="text-xs text-red-500 mt-1">{errors.html}</p>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate("/marketing/email-templates")}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={saving}
          className="bg-[#885ABB] text-white hover:bg-[#6f47a2]"
        >
          {saving ? (isEdit ? "Saving..." : "Creating...") : isEdit ? "Save Changes" : "Create Template"}
        </Button>
      </div>
    </form>
  );
};

export default EmailTemplateForm;

